"use client";

import { useCallback, useEffect, useId, useMemo, useState } from "react";
import {
  KEYBOARD_VIEWBOX_HEIGHT,
  keyboardViewBoxWidth,
  keyCenterX,
  MiniPiano,
} from "@/components/ui/molecules/mini-piano";
import { useActivePianoNotes } from "@/hooks/use-active-piano-notes";
import { useHintPublisher } from "@/hooks/use-hint-publisher";
import { useNoteLabel, useNoteNotation } from "@/hooks/use-notation";
import { useReducedMotion } from "@/hooks/use-reduced-motion";
import { useStudio } from "@/contexts/studio-context";
import type { Hint } from "@/contexts/hint-context";
import type { AnalysisChord } from "@/lib/music/analyzer-types";
import type { LearningJourney } from "@/lib/learning-journey/journey";
import {
  phraseHandShapePlan,
  shapePresses,
  type HandShapePlan,
  type ShapePress,
} from "@/lib/learning-journey/hand-shapes";
import {
  JOURNEY_HINT_COLOR_LEFT_DISCOVER,
  JOURNEY_HINT_COLOR_RIGHT_DISCOVER,
} from "@/hooks/journey/journey-hint-mapping";
import {
  applicableStepIndices,
  phraseStateToStepIndex,
  reachableStepIndices,
  stepIndexToPhraseState,
  stepPosition,
} from "@/lib/learning-journey/step-mapping";
import type {
  Phrase,
  PhraseFSMSnapshot,
  PhraseGrip,
  PhraseNote,
} from "@/lib/learning-journey/types";
import {
  buildPhraseChords,
  chordIntervals,
  type PhraseChord,
  phraseKeyboardWindow,
  type KeyboardWindow,
} from "../phrase-chords";
import { chordProseLabel } from "../s0-copy";
import {
  coverPlace,
  evaluateGrip,
  evaluatePlaceWalk,
  evaluateOpening,
  fingerMark,
  fingerWordOn,
  fingersOn,
  gripKeyboardSize,
  gripNudge,
  gripStatusMessage,
  gripSuccess,
  huntHeadline,
  walkHeadline,
  nextPlaceHeadline,
  nextShapeLine,
  anchorKeys,
  openingKeys,
  openingNudge,
  opensWhole,
  orientationHeadline,
  orientationHuntHeadline,
  placeTakenLine,
  takenHeadline,
  wholeHeadline,
  wideGapLine,
  recurrenceLine,
  type GripHeadline,
  noteLabellers,
  announcedChordOver,
  announcedRootAt,
  chordRoleLine,
  splitOctave,
} from "../grip";
import {
  ARC_Y,
  FINGER_Y,
  regionTint,
} from "../shell/keyboard-marks";
import { FloatingHeader } from "../shell/floating-header";
import { FloatingShell } from "../shell/floating-shell";
import { HeroRuns } from "../shell/hero-runs";
import { MiniSteps } from "../shell/mini-steps";
import { SkipHandButton } from "../shell/skip-hand-button";

/**
 * View for `S1a` / `S2a`: **la prise**, the hand settling before it plays.
 *
 * The step this replaces walked the hand's distinct pitches one at a time,
 * low to high. It showed the note name *and* lit the key, so nothing was
 * recalled; isolated presses train no transition; and its low→high order
 * exists nowhere in the piece, so the player executed a sequence the music
 * never asks for.
 *
 * ## What it shows: shapes, not places
 *
 * The unit is a **grip** — a stretch during which no finger changes key —
 * and grips that share a configuration are folded into one shape. Four
 * fifths fingered 5-1 at four pitches are one thing to learn, shown once and
 * executed once, at the first place. The other places are drawn on the
 * roll; moving between them is S1b's job.
 *
 * ## What it says: one order, large
 *
 * Every screen is an order set big enough to be read while looking at the
 * keys — « Index sur Mi4 », « Garde l'index enfoncé » — with one line under
 * it. The keyboard carries the rest. See `GripHeadline`.
 *
 * ## Two acts, sometimes one
 *
 * **Find**, then **complete**. The panel names what the roll drops first in
 * this place, with its finger, and lights nothing: locating one named key
 * among 88 is the retrieval the old step never asked for. Once it is down,
 * the rest of the shape lights and the hand opens onto it. A place that
 * opens on its whole shape — a plaqué chord — has nothing to spread onto and
 * is a single act.
 *
 * ## What it claims
 *
 * A set of keys, a finger on each, that the hand holds them together, and
 * how many places the phrase uses that configuration. All of it is true at
 * every instant of the grip: inside one, the assignment is constant by
 * construction. Finger numbers are therefore safe here and were not under
 * the previous, reach-based unit — where the panel could and did draw a
 * region that had existed at no instant.
 *
 * ## What counts as done
 *
 * The held keys equal the place, at any instant. No time window: taking a
 * chord deliberately is the correct beginner gesture, and a stopwatch would
 * teach hurry. Timers here only decide when to speak.
 */

const PANEL_WIDTH_PX = 600;
const PANEL_PADDING_X_PX = 32;

/** The order at the top of the panel. See `GripHeadline`. */
const HERO_FONT_PX = 38;

/**
 * Height the main keyboard takes when nothing bounds it. A maximum, not a
 * size: the drawing keeps the aspect ratio of the window it shows, so past
 * three octaves the height is what has to give — see `KEYBOARD_MAX_W_PX`.
 */
const KEYBOARD_H_PX = 128;

/**
 * Width the keyboard may not exceed.
 *
 * The drawing used to be sized from its height alone, and the panel clipped
 * whatever ran past its edge: a four-octave window at 96 px tall is 672 px
 * wide against 504 available, so the top third of the keyboard was simply
 * cut off. Keys of the shape that fell in it disappeared while the copy went
 * on naming them with their fingers — the panel and the drawing disagreeing,
 * which is the defect this whole step was rebuilt to remove.
 *
 * Same rule the shapes map already followed: the cell is sized to fit the
 * panel, never the other way round.
 */
const KEYBOARD_MAX_W_PX = PANEL_WIDTH_PX - PANEL_PADDING_X_PX * 2;

/**
 * Size of the key names under the keyboard, in **screen px**.
 *
 * `MiniPiano` writes its labels inside the keys, sized in viewBox units: a
 * two-octave window renders them near 15px, three near 10px, four under 8px
 * — and four octaves is the window a two-handed phrase usually opens. So on
 * the screens showing the widest hands the names were unreadable, and the
 * only place they were stated was the sub-line, as prose: « La#-Fa, octaves
 * 2 et 3, doigté 5-1 ». Which keys to play is what this step is about, and
 * it was the one thing the drawing did not say.
 *
 * A size the window cannot shrink puts the name under the key it names,
 * next to the finger that plays it.
 */
const KEY_NAME_FONT_PX = 13;

/** Height of one row of names. A second row exists only for close keys. */
const KEY_NAME_ROW_H_PX = 17;

/** Rough advance width of one character, to tell colliding names apart. */
const KEY_NAME_CHAR_W_RATIO = 0.62;

/** Air kept between two names sharing a row, in px. */
const KEY_NAME_GAP_PX = 6;

/**
 * Reserved height for the status line. Fixed on purpose: without it every
 * error animates a resize of the shell, and the panel jumps under the hand
 * of someone trying to keep four fingers still.
 */
const MESSAGE_BOX_H_PX = 40;

/** Nothing taken yet. A shared empty set, so a reset compares by reference. */
const NO_KEYS: ReadonlySet<number> = new Set<number>();

const ADVANCE_MS = 700;

/**
 * How long the hunt holds once the key is under the finger.
 *
 * Act 1 latched into act 2 on the same frame: the headline naming the key
 * vanished at the instant it was struck, and the only thing the player could
 * infer from a screen that changed is that *something* happened. Half a
 * second is not a delay to sit through — it is the beat that turns a
 * disappearance into an answer, and it is under the second where waiting
 * starts being felt.
 */
const FOUND_MS = 550;
/** Silence before the step offers help. */
const NUDGE_MS = 1100;

const HINT_SOURCE = "journey-grip";

type GripStateProps = {
  readonly phrase: Phrase;
  readonly phraseSnapshot: PhraseFSMSnapshot;
  readonly hand: "left" | "right";
  readonly chords: readonly AnalysisChord[];
  readonly journey: LearningJourney;
  readonly onPositionConfirmed: () => void;
};

export function GripState({
  phrase,
  phraseSnapshot,
  hand,
  chords,
  journey,
  onPositionConfirmed,
}: GripStateProps) {
  const titleId = useId();
  const { studio } = useStudio();
  const reducedMotion = useReducedMotion();
  const toNoteLabel = useNoteLabel();

  const plan = useMemo(() => phraseHandShapePlan(phrase, hand), [phrase, hand]);

  const presses = useMemo(() => shapePresses(plan), [plan]);
  const total = plan.kind === "orientation" ? 1 : presses.length;
  const index = Math.min(
    Math.max(phraseSnapshot.positionIndex, 0),
    Math.max(total - 1, 0),
  );
  const press: ShapePress | undefined = presses[index];

  // The window S0 just used. Keeping it means the region appears on the same
  // keys of the same drawing the player was looking at one screen ago.
  const phraseChords = useMemo(
    () => buildPhraseChords(chords, phrase),
    [chords, phrase],
  );
  const keyboardWindow = useMemo<KeyboardWindow>(
    () => phraseKeyboardWindow(phraseChords),
    [phraseChords],
  );

  const handColor =
    hand === "left" ? "var(--color-hand-left)" : "var(--color-hand-right)";

  const stepIndex = phraseStateToStepIndex(phraseSnapshot.state);
  // What this unit's stepper counts: the steps it actually has. A
  // right-hand-only unit has three, not five with two dead slots.
  const steps = useMemo(
    () => applicableStepIndices(phrase, phraseSnapshot.chains),
    [phrase, phraseSnapshot.chains],
  );
  const reachable = useMemo(
    () =>
      reachableStepIndices(
        phrase,
        phraseSnapshot.maxState,
        phraseSnapshot.chains,
      ),
    [phrase, phraseSnapshot.maxState, phraseSnapshot.chains],
  );

  const handleJump = useCallback(
    (idx: number) => {
      const target = stepIndexToPhraseState(idx);

      if (target === null) {
        return;
      }

      journey.revisitPhraseState(target);
    },
    [journey],
  );

  const handleBack = useCallback(() => {
    journey.stepBack();
  }, [journey]);

  // Leaving the hand's steps from its grip: see `handSkipTarget`.
  const handleSkipHand = useCallback(() => {
    journey.skipHand();
  }, [journey]);

  // One scroll per place, then the roll is frozen: we are asking for a
  // motor act, not for a moving target to be read.
  //
  // Per place and not once per unit: the region is lit over the span the
  // hand is on it, and a later place can sit several bars down the roll —
  // measured, up to ten. Lighting keys the player is not looking at is
  // worse than a scroll between two acts, and the freeze that matters is
  // the one *during* an act, which this keeps.
  const scrollTick =
    press?.place.startTick ??
    (plan.kind === "orientation" ? plan.firstGrip.startTick : undefined);

  useEffect(() => {
    if (!studio || scrollTick === undefined) {
      return;
    }

    void studio.animateScrollToTick(scrollTick);
  }, [studio, scrollTick]);

  useEffect(() => {
    if (!studio) {
      return;
    }

    return () => {
      studio.clearHintSelection();
    };
  }, [studio]);

  // No third state: a hand that plays gets shapes or orientation, and a
  // hand that does not play never reaches this view at all.
  if (plan.kind === "empty") {
    return null;
  }

  // Which shape, or which place of it: a standing fact, so it sits in the
  // header rather than costing a row of the body.
  const label = stepLabel(plan, press);

  return (
    <FloatingShell
      variant="floating"
      width={PANEL_WIDTH_PX}
      ariaLabel={hand === "left" ? "Prise main gauche" : "Prise main droite"}
      titleId={titleId}
      contentKey={`grip-${hand}-${index}`}
    >
      <FloatingHeader
        stepIndex={stepPosition(steps, stepIndex)}
        stepTotal={steps.length}
        kicker="PRISE"
        titleId={titleId}
        trailing={
          label !== undefined && (
            <span
              className="font-mono text-[11px] uppercase shrink-0"
              style={{
                color: "var(--color-text-muted)",
                letterSpacing: "0.16em",
              }}
            >
              {label}
            </span>
          )
        }
      />

      <GripBody
        key={`${phrase.id}:${hand}:${index}`}
        hand={hand}
        handColor={handColor}
        plan={plan}
        press={press}
        notes={phrase.notesByHand[hand]}
        keyboardWindow={keyboardWindow}
        phraseChords={phraseChords}
        reducedMotion={reducedMotion}
        toNoteLabel={toNoteLabel}
        onConfirmed={onPositionConfirmed}
      />

      {phraseSnapshot.handSkipTarget !== null && (
        <div
          className="flex"
          style={{
            justifyContent: "flex-end",
            padding: `0 ${PANEL_PADDING_X_PX - 12}px 10px`,
          }}
        >
          <SkipHandButton
            target={phraseSnapshot.handSkipTarget}
            onSkip={handleSkipHand}
          />
        </div>
      )}

      <MiniSteps
        active={stepIndex}
        steps={steps}
        reachable={reachable}
        onJump={handleJump}
        onBack={phraseSnapshot.canStepBack ? handleBack : undefined}
      />
    </FloatingShell>
  );
}

/**
 * What the mini-stepper writes under the panel.
 *
 * Places before shapes: while a shape is being taken at several places, the
 * useful count is which place, not which shape — the shape has not changed.
 */
function stepLabel(
  plan: HandShapePlan,
  press: ShapePress | undefined,
): string | undefined {
  if (plan.kind === "orientation") {
    return "REPÈRES";
  }

  if (plan.kind !== "shapes" || press === undefined) {
    return undefined;
  }

  if (press.placeCount > 1) {
    return `PLACE ${press.placeIndex + 1}/${press.placeCount}`;
  }

  if (plan.shapes.length > 1) {
    return `FORME ${press.shapeIndex + 1}/${plan.shapes.length}`;
  }

  return undefined;
}

// ── Body ──────────────────────────────────────────────────────────

type GripBodyProps = {
  readonly hand: "left" | "right";
  readonly handColor: string;
  readonly plan: HandShapePlan;
  readonly press: ShapePress | undefined;
  readonly notes: readonly PhraseNote[];
  readonly keyboardWindow: KeyboardWindow;
  /** What S0 announced, so a root is only ever restated, never invented. */
  readonly phraseChords: readonly PhraseChord[];
  readonly reducedMotion: boolean;
  readonly toNoteLabel: (name: string) => string;
  readonly onConfirmed: () => void;
};

function GripBody({
  hand,
  handColor,
  plan,
  press,
  notes,
  keyboardWindow,
  phraseChords,
  reducedMotion,
  toNoteLabel,
  onConfirmed,
}: GripBodyProps) {
  const { studio } = useStudio();
  const active = useActivePianoNotes();

  // Orientation asks for one key — the one the hand starts on — and then
  // states the territory. Not a formality: finding it is the same retrieval
  // act, and it is the only thing the panel can honestly ask of a hand that
  // never settles.
  const orienting = plan.kind === "orientation";
  const place = press ? press.place : orienting ? plan.firstGrip : undefined;
  const first = press === undefined || press.placeIndex === 0;

  const labels = useMemo(() => noteLabellers(toNoteLabel), [toNoteLabel]);
  const noteLabel = labels.key;
  // What a screen reader hears. « Si4 » is read out as "si quatre", which
  // carries exactly the fingering ambiguity the display sets apart.
  const spokenKey = useCallback(
    (midi: number) => {
      const parts = splitOctave(noteLabel(midi));

      return parts === null
        ? noteLabel(midi)
        : `${parts.name} octave ${parts.octave}`;
    },
    [noteLabel],
  );
  const pitchClassLabel = labels.pitchClass;
  // What the keyboard writes under a key it draws. The pitch class alone:
  // the key is drawn where it is and the landmarks under it say which
  // octave, so spelling it out would be the widest label for the least news.
  const keyName = useCallback(
    (midi: number) => pitchClassLabel(((midi % 12) + 12) % 12),
    [pitchClassLabel],
  );
  const notation = useNoteNotation();
  const announcedRoot =
    place === undefined ? null : announcedRootAt(phraseChords, place.startTick);

  /**
   * What the keys under the hand are inside the chord the piece is playing.
   *
   * Per place and not per shape, which is the whole interest of it: the shape
   * does not change from one place to the next, the harmony does. See
   * `chordRoleLine`, and `shapeName` for why the chord stays out of the
   * headline.
   */
  const harmony = useMemo(() => {
    if (place === undefined || orienting) {
      return null;
    }

    const window = announcedChordOver(
      phraseChords,
      place.startTick,
      place.endTick,
    );

    if (window === null) {
      return null;
    }

    const intervals = chordIntervals(window.quality);

    if (intervals === null) {
      return null;
    }

    return chordRoleLine(place, {
      rootPitchClass: window.rootPitchClass,
      intervals,
      name: chordProseLabel({ chord: window }, notation),
    });
  }, [place, orienting, phraseChords, notation]);

  const opening = useMemo(
    () => (place === undefined ? [] : openingKeys(place, notes)),
    [place, notes],
  );
  const whole = place !== undefined && !orienting && opensWhole(place, opening);

  /**
   * The key both acts are built on.
   *
   * The **thumb's**, not the one the piece plays first. See `anchorKeys`.
   * Two exceptions keep the musical opening: a place the piece strikes whole
   * has no anchor to hold, it is taken in one gesture; and the orientation
   * branch is about the territory the hand travels, not about a position.
   */
  const anchor = useMemo(
    () =>
      place === undefined || whole || orienting ? opening : anchorKeys(place),
    [place, whole, orienting, opening],
  );

  // Act 1 latches into act 2: once the opening keys have been found they stay
  // found, otherwise lifting a finger to complete the shape would send the
  // player back to hunting for a key they just located.
  const [found, setFound] = useState(false);
  // The beat between finding the key and being asked for the shape. Only
  // when the hunt happened: a place that opens whole never had an act 1.
  const [settling, setSettling] = useState(false);
  const openingEval = evaluateOpening(anchor, active);

  if (openingEval.phase === "complete" && !found) {
    setFound(true);

    if (first && !whole) {
      setSettling(true);
    }
  }

  useEffect(() => {
    if (!settling) {
      return;
    }

    const timer = setTimeout(() => setSettling(false), FOUND_MS);

    return () => clearTimeout(timer);
  }, [settling]);

  /**
   * Keys of the shape already taken over the held opening.
   *
   * Accumulated rather than read off the instant: the shape is walked key by
   * key, so a key struck and released three seconds ago has been taken. Reset
   * when the place changes, which is a change of subject and not a mutation
   * of this one.
   */
  const [covered, setCovered] = useState<ReadonlySet<number>>(NO_KEYS);
  const [coveredPlace, setCoveredPlace] = useState<PhraseGrip | null>(null);

  if (place !== undefined && coveredPlace !== place) {
    setCoveredPlace(place);
    setCovered(NO_KEYS);
  }

  // Fed from the instrument rather than from a render-time value: what is
  // taken is an event, and an effect that recomputed it from the currently
  // held set would be reacting to its own render.
  useEffect(() => {
    if (studio === null || place === undefined || whole || orienting) {
      return;
    }

    const piano = studio.getPiano();

    return piano.on((event) => {
      if (event.type !== "press" || event.origin !== "player") {
        return;
      }

      const down = new Set(piano.getPressedMidis().keys());

      setCovered((prev) => coverPlace(place, down, prev));
    });
  }, [studio, place, whole, orienting]);

  const evaluation =
    place === undefined
      ? openingEval
      : orienting
        ? openingEval
        : whole
          ? evaluateGrip(place, active)
          : evaluatePlaceWalk(place, anchor, active, covered);

  // Same latch on the shape, so releasing a key during the 700 ms before the
  // FSM advances cannot undo the success or cancel the pending advance.
  const [taken, setTaken] = useState(false);

  if (found && evaluation.phase === "complete" && !taken) {
    setTaken(true);
  }

  useEffect(() => {
    if (!taken) {
      return;
    }

    const timer = setTimeout(onConfirmed, ADVANCE_MS);

    return () => clearTimeout(timer);
  }, [taken, onConfirmed]);

  const [nudging, setNudging] = useState(false);

  // Help arrives on its own after a silence, never on a visible countdown: a
  // timer on screen makes a motor act hurried, which is the opposite of what
  // this step installs. Only ever armed here; whether it shows is derived.
  useEffect(() => {
    if (taken) {
      return;
    }

    const timer = setTimeout(() => setNudging(true), NUDGE_MS);

    return () => clearTimeout(timer);
  }, [taken, found, evaluation.pressed.length, openingEval.extra.length]);

  // The roll and the studio keyboard show the shape only once it has been
  // revealed. Lighting it during the hunt would hand over the answer. A place
  // that opens whole has nothing to hide, so it is revealed from the start.
  //
  // And the hunt happens once per *shape*, not once per place: at the second
  // place the player is being asked to move a configuration they have
  // already retrieved, so making them find it again is the note-by-note
  // repetition this step replaced.
  const revealed = found || whole || !first;

  // Every C of the window, named. A pianist does not find a key in the void —
  // they count from a C — so the drawing needs its references.
  //
  // They used to be withheld on the key being looked for, so that a C could
  // not be read off its own label. That was the riddle talking: the key is
  // now drawn where it is, and hiding the name of the one key the panel is
  // naming would be the only inconsistency left on screen.
  const landmarks = useMemo(() => {
    const map = new Map<number, string>();

    for (let i = 0; i < keyboardWindow.octaves; i += 1) {
      const midi = keyboardWindow.lowMidi + i * 12;

      map.set(midi, noteLabel(midi));
    }

    return map;
  }, [keyboardWindow, noteLabel]);

  const hintColor =
    hand === "left"
      ? JOURNEY_HINT_COLOR_LEFT_DISCOVER
      : JOURNEY_HINT_COLOR_RIGHT_DISCOVER;

  // Orientation lights only the key it names: lighting the whole travelled
  // range would assert a hand covering it, which is what it is telling the
  // player is impossible.
  const litMidis = useMemo(() => {
    if (place === undefined) {
      return [];
    }

    if (orienting) {
      return opening;
    }

    // Before the reveal, the key the hand anchors on. It used to be nothing
    // at all, because the step was a hunt and lighting it would have handed
    // over the answer. There is no answer to hand over any more: the panel
    // names the key, so the roll and the instrument show where it is.
    return revealed ? [...place.midis] : [...anchor];
  }, [place, revealed, orienting, opening, anchor]);

  useEffect(() => {
    if (!studio) {
      return;
    }

    if (place === undefined || litMidis.length === 0) {
      studio.clearHintSelection();

      return;
    }

    studio.setHintSelection([...litMidis], place.startTick, place.endTick);
  }, [studio, litMidis, place]);

  const hint = useMemo<Hint>(
    () => ({
      source: HINT_SOURCE,
      midis: litMidis.map((midi) => ({ midi, color: hintColor })),
    }),
    [litMidis, hintColor],
  );

  useHintPublisher(hint);

  if (place === undefined) {
    return null;
  }

  const shapes = plan.kind === "shapes" ? plan.shapes : [];
  const lastPress =
    press === undefined ||
    (press.shapeIndex === shapes.length - 1 &&
      press.placeIndex === press.placeCount - 1);
  const lastOfShape =
    press === undefined || press.placeIndex === press.placeCount - 1;

  const successLine = orienting
    ? "C'est le point de départ."
    : !lastOfShape
      ? placeTakenLine()
      : lastPress && press !== undefined
        ? gripSuccess(hand, press.shape, shapes.length)
        : nextShapeLine();

  // The shape appears once the beat confirming the key is over. The roll and
  // the landmarks light straight away — that is the reward for finding it —
  // but the panel does not ask the next thing while it is still answering
  // the last one.
  const showShape = revealed && !settling;

  // Act 1 stays up for `FOUND_MS` after the key is struck, with its line
  // turned into an answer — see `settling`.
  const hunting = first && (!revealed || settling);

  // Act 2 of a shape the piece does not strike whole: the anchor found, the
  // rest walked, in any order. See `walkHeadline`.
  const adding =
    first && !whole && !orienting && showShape && press !== undefined && !taken;

  const baseHeadline: GripHeadline = taken
    ? takenHeadline(successLine)
    : hunting
      ? orienting
        ? orientationHuntHeadline(place, anchor, noteLabel)
        : huntHeadline(place, anchor, noteLabel)
      : adding
        ? walkHeadline(place, anchor, covered, noteLabel)
        : plan.kind === "orientation"
          ? orientationHeadline(hand, plan.lowMidi, plan.highMidi, noteLabel)
          : press !== undefined && !first
            ? nextPlaceHeadline(
                press.shape,
                place,
                press.placeIndex,
                pitchClassLabel,
              )
            : wholeHeadline(place, pitchClassLabel, announcedRoot);

  const headline: GripHeadline =
    settling && !taken
      ? {
          ...baseHeadline,
          sub: anchor.length > 1 ? "Ce sont elles." : "C'est elle.",
        }
      : baseHeadline;

  // What the count under the keyboard counts: the keys walked over the
  // anchor, not the anchor itself.
  const rest = place.midis.filter((midi) => !anchor.includes(midi));
  const foundCount = rest.filter((midi) => covered.has(midi)).length;

  // Stated once, on the place that introduces the shape. Repeating "it comes
  // back four times" on each of the four would read as four announcements of
  // the same fact, which is the click-through this deduplication exists to
  // avoid.
  const recurrence =
    press !== undefined && first && showShape
      ? recurrenceLine(press.shape)
      : null;

  // Named on the screen that covers the unit containing it, once the shape
  // is up — during the hunt it would be a second thing to read before the
  // first is done. It names its own keys: it is never about the shape on
  // screen.
  const gapLine =
    showShape && plan.kind !== "empty"
      ? wideGapLine(plan.wideGaps, noteLabel)
      : null;

  const harmonyLine = showShape ? harmony : null;

  const message =
    taken || settling
      ? ""
      : revealed
        ? (gripStatusMessage(evaluation, noteLabel) ??
          (nudging ? gripNudge(place, evaluation, noteLabel) : null) ??
          "")
        : ((nudging ? openingNudge(anchor, active, noteLabel) : null) ?? "");

  return (
    <div style={{ padding: `26px ${PANEL_PADDING_X_PX}px 20px` }}>
      <div aria-live="polite">
        <p
          className="font-mono"
          style={{
            margin: 0,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: handColor,
          }}
        >
          {hand === "left" ? "Main gauche" : "Main droite"}
        </p>
        <p
          style={{
            margin: "10px 0 0",
            fontSize: HERO_FONT_PX,
            lineHeight: 1.08,
            fontWeight: 600,
            letterSpacing: "-0.02em",
            color: "var(--color-text-primary)",
            textWrap: "balance",
          }}
        >
          <HeroRuns hero={headline.hero} accentColor={handColor} />
        </p>
        <p
          style={{
            margin: "10px 0 0",
            fontSize: 17,
            lineHeight: 1.45,
            color: "var(--color-text-secondary)",
            textWrap: "pretty",
          }}
        >
          {headline.sub}
        </p>
      </div>

      <GripKeyboard
        place={place}
        // Act 1 draws the one key it is about; the shape arrives with the
        // reveal. Orientation draws the key it names and never the range —
        // lighting the whole travelled span would assert a hand covering it.
        onlyMidis={orienting ? opening : showShape ? undefined : anchor}
        keyboardWindow={keyboardWindow}
        landmarks={landmarks}
        keyName={keyName}
        handColor={handColor}
        maxHeightPx={KEYBOARD_H_PX}
        pressed={showShape ? evaluation.pressed : openingEval.pressed}
        extra={showShape ? evaluation.extra : openingEval.extra}
        revealed={showShape}
        taken={taken}
        reducedMotion={reducedMotion}
      />

      <div
        aria-live="polite"
        style={{
          minHeight: MESSAGE_BOX_H_PX,
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginTop: 16,
          fontSize: 14,
          lineHeight: 1.5,
          color: "var(--color-text-muted)",
          textWrap: "pretty",
        }}
      >
        {adding ? (
          <FoundCount
            found={foundCount}
            total={rest.length}
            handColor={handColor}
            message={message}
          />
        ) : (
          message
        )}
      </div>

      {(harmonyLine !== null || recurrence !== null || gapLine !== null) && (
        <div style={{ marginTop: 4 }}>
          {harmonyLine !== null && (
            <p
              style={{
                margin: 0,
                fontSize: 13,
                lineHeight: 1.5,
                color: "var(--color-text-muted)",
                textWrap: "pretty",
              }}
            >
              {harmonyLine}
            </p>
          )}
          {recurrence !== null && (
            <p
              style={{
                margin: harmonyLine !== null ? "4px 0 0" : 0,
                fontSize: 13,
                lineHeight: 1.5,
                color: "var(--color-text-muted)",
                textWrap: "pretty",
              }}
            >
              {recurrence}
            </p>
          )}
          {gapLine !== null && (
            <p
              style={{
                margin:
                  recurrence !== null || harmonyLine !== null ? "4px 0 0" : 0,
                fontSize: 13,
                lineHeight: 1.5,
                color: "var(--color-warning, #f5a524)",
                textWrap: "pretty",
              }}
            >
              {gapLine}
            </p>
          )}
        </div>
      )}

      <span className="sr-only">
        {revealed
          ? `Prise main ${hand === "left" ? "gauche" : "droite"} : ${place.midis
              .map((midi) => {
                const mark = fingerMark(fingersOn(place, midi));

                return mark === ""
                  ? noteLabel(midi)
                  : `${noteLabel(midi)} doigt ${mark}`;
              })
              .join(", ")}.`
          : `Pose ${fingerWordOn(place, anchor)} sur ${anchor
              .map(spokenKey)
              .join(", ")}.`}
      </span>
    </div>
  );
}

// ── Found count ───────────────────────────────────────────────────

/**
 * How many of the keys to add are taken: one dot per key, and the count.
 *
 * The words next to it give way to the status line when there is something
 * to correct, so an error never pushes the count off the row.
 */
function FoundCount({
  found,
  total,
  handColor,
  message,
}: {
  readonly found: number;
  readonly total: number;
  readonly handColor: string;
  readonly message: string;
}) {
  return (
    <>
      <span aria-hidden style={{ display: "flex", gap: 6, flexShrink: 0 }}>
        {Array.from({ length: total }, (_, i) => (
          <i
            key={i}
            style={{
              width: 9,
              height: 9,
              borderRadius: "50%",
              border: `1.5px solid ${handColor}`,
              background: i < found ? handColor : "transparent",
              opacity: i < found ? 1 : 0.55,
            }}
          />
        ))}
      </span>
      <span
        className="font-mono tabular-nums"
        style={{
          flexShrink: 0,
          fontSize: 18,
          fontWeight: 700,
          color: "var(--color-text-primary)",
        }}
      >
        {found}
        <span style={{ color: "var(--color-text-muted)" }}>/{total}</span>
      </span>
      <span>
        {message !== ""
          ? message
          : "trouvées — ce que tu trouves reste acquis."}
      </span>
    </>
  );
}

// ── Keyboard + arc ────────────────────────────────────────────────

type GripKeyboardProps = {
  readonly place: PhraseGrip;
  /**
   * Keys to draw instead of the whole grip. Orientation passes the single
   * key it names: lighting the travelled range would assert a hand covering
   * it, which is precisely what it is telling the player is impossible.
   */
  readonly onlyMidis?: readonly number[];
  readonly keyboardWindow: KeyboardWindow;
  readonly landmarks: ReadonlyMap<number, string>;
  /** Names a key of the shape. Pitch class: the drawing says where it is. */
  readonly keyName: (midi: number) => string;
  readonly handColor: string;
  /** Upper bound; the width bound may lower it. */
  readonly maxHeightPx: number;
  readonly pressed: readonly number[];
  readonly extra: readonly number[];
  readonly revealed: boolean;
  readonly taken: boolean;
  readonly reducedMotion: boolean;
};

/** The arc sits above the keys: it is the abstraction of the palm. */

/** Where the finger numbers sit, in viewBox units above the key bottoms. */

/**
 * How much hand colour a shape key carries while it waits to be pressed.
 *
 * Mixed against the key's **own** resting colour, not always against white.
 * An earlier version mixed 18 % toward `#ffffff` for every key: on a white
 * key — resting at `rgba(255,255,255,0.92)` — that lands on rgb(221,245,243)
 * for the left hand, a pale wash its neighbours are within a few points of,
 * so the shape the whole step is about was invisible. And on a black key
 * the same rule went the other way, making a *waiting* key brighter against
 * its surroundings than a *pressed* one.
 *
 * Mixing against the key's own base keeps one relation true on both kinds:
 * pressed is the most saturated thing on the keyboard, waiting is a legible
 * step below it, and resting is untouched.
 */

function GripKeyboard({
  place,
  onlyMidis,
  keyboardWindow,
  landmarks,
  keyName,
  handColor,
  maxHeightPx,
  pressed,
  extra,
  revealed,
  taken,
  reducedMotion,
}: GripKeyboardProps) {
  const { lowMidi, octaves } = keyboardWindow;
  const viewWidth = keyboardViewBoxWidth(octaves);
  const { widthPx, heightPx } = gripKeyboardSize(
    viewWidth,
    KEYBOARD_VIEWBOX_HEIGHT,
    maxHeightPx,
    KEYBOARD_MAX_W_PX,
  );

  const shapeMidis = onlyMidis ?? place.midis;

  // Everything this act is about, plus whatever the hand is doing. Before
  // the reveal `shapeMidis` is the single key the hand anchors on, not the
  // whole shape — the step shows where to put a finger and then what to
  // build on it, and neither half is a riddle.
  const lit = [...new Set([...shapeMidis, ...pressed, ...extra])];

  const down = useMemo(() => new Set(pressed), [pressed]);

  const midiColors = useMemo(() => {
    const map = new Map<number, string>();

    for (const midi of shapeMidis) {
      map.set(midi, down.has(midi) ? handColor : regionTint(midi, handColor));
    }

    for (const midi of extra) {
      map.set(midi, "var(--color-warning, #f5a524)");
    }

    return map;
  }, [shapeMidis, down, extra, handColor]);

  // `keyCenterX` answers null outside the window, and the window is capped
  // at four octaves — so a shape reaching past it loses keys here. Dropping
  // them and drawing the arc anyway would span the two survivors and assert
  // a hand narrower than the one the copy just named. The arc is a claim
  // about the whole shape or it is not drawn.
  const centres = shapeMidis.map((midi) => keyCenterX(midi, lowMidi, octaves));
  const xs = centres.every((x): x is number => x !== null) ? centres : [];

  // One label per key, drawn as an overlay rather than through MiniPiano: the
  // number has to read against both a tinted white key and a tinted black
  // one, which is a two-colour decision this screen owns.
  const fingerLabels = shapeMidis
    .map((midi) => ({
      midi,
      x: keyCenterX(midi, lowMidi, octaves),
      mark: fingerMark(fingersOn(place, midi)),
    }))
    .filter((f) => f.x !== null && f.mark !== "");

  // The same keys, named, in a row under the keyboard.
  //
  // Under rather than on: a name written on a key is sized by the window —
  // see `KEY_NAME_FONT_PX` — and the widest windows are the ones that need
  // it most. Sized in px it holds at any window, and the only thing left to
  // handle is two names that would be written over each other: a shape
  // holding a semitone puts its two key centres about 10px apart.
  const keyNames = useMemo(() => {
    const scale = viewWidth === 0 ? 1 : widthPx / viewWidth;
    // Right edge of the last name placed on each row.
    const rowRight = [-Infinity, -Infinity];

    return shapeMidis
      .map((midi) => ({
        midi,
        x: keyCenterX(midi, lowMidi, octaves),
        name: keyName(midi),
      }))
      .filter(
        (k): k is { midi: number; x: number; name: string } => k.x !== null,
      )
      .sort((a, b) => a.x - b.x)
      .map((k) => {
        const half =
          (k.name.length * KEY_NAME_FONT_PX * KEY_NAME_CHAR_W_RATIO) / 2;
        const centre = k.x * scale;
        // The top row while it is free, the row under it otherwise. A third
        // name clearing neither takes the row ending furthest left: no hand
        // shape has produced that, and three labels struck on one line is
        // the one outcome worth ruling out anyway.
        const row =
          centre - half >= rowRight[0] + KEY_NAME_GAP_PX
            ? 0
            : centre - half >= rowRight[1] + KEY_NAME_GAP_PX
              ? 1
              : rowRight[0] <= rowRight[1]
                ? 0
                : 1;

        rowRight[row] = centre + half;

        return {
          midi: k.midi,
          name: k.name,
          leftPct: (k.x / viewWidth) * 100,
          row,
        };
      });
  }, [shapeMidis, lowMidi, octaves, viewWidth, widthPx, keyName]);

  const nameRows = keyNames.some((k) => k.row === 1) ? 2 : 1;

  return (
    <div style={{ width: widthPx, margin: "24px auto 0" }}>
      <div style={{ position: "relative", width: "100%", height: heightPx }}>
        <MiniPiano
          midis={lit}
          lowMidi={lowMidi}
          octaves={octaves}
          midiColors={midiColors}
          landmarks={landmarks}
          style={{ width: "100%", height: "100%" }}
        />

        <svg
          viewBox={`0 0 ${viewWidth} ${KEYBOARD_VIEWBOX_HEIGHT}`}
          preserveAspectRatio="xMidYMid meet"
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
          }}
        >
          {revealed && xs.length > 1 && (
            <>
              {/* Dark casing under the arc, same idiom as `PitchRail`: it
                  crosses white keys and black ones, and a single stroke legible
                  on one is invisible on the other. */}
              <line
                x1={xs[0]}
                y1={ARC_Y}
                x2={xs[xs.length - 1]}
                y2={ARC_Y}
                stroke="rgba(0,0,0,0.55)"
                strokeWidth={3.5}
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
              />
              <line
                x1={xs[0]}
                y1={ARC_Y}
                x2={xs[xs.length - 1]}
                y2={ARC_Y}
                stroke={handColor}
                strokeWidth={1.5}
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
                opacity={taken ? 0.9 : 0.25}
                style={
                  reducedMotion || taken
                    ? { transition: "opacity 200ms ease-out" }
                    : {
                        animation:
                          "grip-arc-breathe 2400ms ease-in-out infinite",
                      }
                }
              />
            </>
          )}

          {fingerLabels.map((f) => (
            <g key={`finger-${f.midi}`}>
              <circle
                cx={f.x ?? 0}
                cy={FINGER_Y}
                r={f.mark.length > 1 ? 4.4 : 3.4}
                fill="rgba(0,0,0,0.62)"
              />
              <text
                x={f.x ?? 0}
                y={FINGER_Y}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={f.mark.length > 1 ? 4 : 5}
                fontWeight={700}
                style={{ fill: "#ffffff", letterSpacing: "-0.02em" }}
              >
                {f.mark}
              </text>
            </g>
          ))}
        </svg>

        {taken && (
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: -6,
              borderRadius: 10,
              boxShadow: reducedMotion
                ? "inset 0 0 0 1px rgba(255,255,255,0.9)"
                : "0 0 24px rgba(255,255,255,0.55), 0 0 48px rgba(255,255,255,0.25)",
              transition: "box-shadow 220ms ease-out",
              pointerEvents: "none",
            }}
          />
        )}
      </div>

      {/* Said by the panel above too, so a reader hears it once. */}
      <div
        aria-hidden
        style={{
          position: "relative",
          height: nameRows * KEY_NAME_ROW_H_PX,
          marginTop: 6,
        }}
      >
        {keyNames.map((k) => (
          <span
            key={`name-${k.midi}`}
            style={{
              position: "absolute",
              left: `${k.leftPct}%`,
              top: k.row * KEY_NAME_ROW_H_PX,
              transform: "translateX(-50%)",
              fontSize: KEY_NAME_FONT_PX,
              lineHeight: `${KEY_NAME_ROW_H_PX}px`,
              fontWeight: 700,
              letterSpacing: "-0.01em",
              whiteSpace: "nowrap",
              // Lit like the key it names: waiting, then the hand's colour
              // once the key is down. The drawing and its legend say the
              // same thing at the same instant, or the legend is noise.
              color: down.has(k.midi)
                ? handColor
                : "var(--color-text-secondary)",
              transition: "color 160ms ease-out",
            }}
          >
            {k.name}
          </span>
        ))}
      </div>

      <style>{`
        @keyframes grip-arc-breathe {
          0%, 100% { opacity: 0.22; }
          50%      { opacity: 0.38; }
        }
      `}</style>
    </div>
  );
}
